import React, {useEffect, useRef, useState} from "react";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { useAppDispatch, useAppSelector } from "src/state/hooks";
import { updateEventRule } from "src/state/slice/EventModelSlice";
import { WithFlyout } from "@amzn/stencil-react-components/flyout";
import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { TestEventRuleExpander } from "src/components/testingComponents/index"
import { JsonLogicAuthoringComponent } from "@amzn/tell-us-app-website-ui-commons";
import * as triggerConfig from "src/config/triggerConfigs.json";
import * as defaultDataAttributes from "src/config/defaultDataAttributes.json";
import { getAvailableDataAttributesInSegmentationService } from "src/service/service";
import "src/components/commonStyles.scss";
import { AvailableDataAttributesFlyoutContent } from "./availableDataAttributesFlyout";
import { AttributeRule } from "src/entity/entities";
import { InputFooter } from "@amzn/stencil-react-components/form";

const STRINGS_DISPLAYED = {
  event_rule_label: "Event Rule",
  event_rule_placeholder: "Define your event rule here as a JSON logic expression.",
  see_data_attributes_button_text: "See available data attributes",
  invalid_event_rule_message: "Invalid Event Rule!",
}

type props = {
  isValidRule: boolean
}

/**
 * Renders the components related to defining event rule,
 * seeing available data attributes to define rules on, and testing the event rules defined.
 */
export const EventRuleComponent = ({isValidRule}:props) => {
  const dispatch = useAppDispatch();
  const eventModelState = useAppSelector(({eventModel}) => eventModel);
  const triggerSpecificAttributes = triggerConfig[`${eventModelState?.clientUseCase}`]?.receivedDataAttributes ?? [];
  const defaultAttributes = defaultDataAttributes.attributeRuleList;

  const [availableDataAttributes, setAvailableDataAttributes] = useState<AttributeRule[]>([...defaultAttributes, ...triggerSpecificAttributes]);

  // Ref to store fetched data and prevent re-fetching
  const attributesFromSSRef = useRef<AttributeRule[] | null>(null);

  // Fetch segmentation service data once and store it in a ref
  useEffect(() => {
    if (!attributesFromSSRef.current) {
      getAvailableDataAttributesInSegmentationService()
          .then((attributesFromSS) => {
            const filteredAttributes = attributesFromSS.filter((attr) => {
                //This is to keep only the attributes that match the event names, this is for Contact Alert attributes.
                if(eventModelState.eventName && attr.attributeName.includes(eventModelState.eventName))
                    return true;

                //This is to remove all other integration tests attributes and contact alert attributes created for other event models.
                return !(attr.description.includes("Derived attribute")
                    && attr.description.includes("for Contact Alert event")
                    || attr.description.includes("Attribute created for integration tests")
              );
            });

            // Store filtered attributes in the ref
            attributesFromSSRef.current = filteredAttributes;

            const segmentationAttributes = [
              ...triggerSpecificAttributes,
              ...filteredAttributes,
            ];
            setAvailableDataAttributes([...new Set(segmentationAttributes)]);
          })
          .catch((_) => {
            //Adding trigger and default attributes, in case SS call has failed. This will allow them to add basic rules.
            setAvailableDataAttributes([...triggerSpecificAttributes, ...defaultAttributes]);
          });
    }
  }, []);

  // Update availableDataAttributes when triggerSpecificAttributes change
  useEffect(() => {
    const segmentationAttributes = [
      ...triggerSpecificAttributes,
      ...(attributesFromSSRef.current || defaultAttributes) //Get either ss attributes or default attributes.
    ];

    // Check if the new attributes are different from the current state
    const newAttributes = [...new Set(segmentationAttributes)];
    if (JSON.stringify(newAttributes) !== JSON.stringify(availableDataAttributes)) {
      setAvailableDataAttributes(newAttributes);
    }
  }, [triggerSpecificAttributes]);


  const onAvailableDataAttributesClick = (openFlyout: () => void) => {
    openFlyout();
  }

  const availableDataAttributesFlyout = ({ close }) => {
    return <AvailableDataAttributesFlyoutContent
        close={close}
        dataAttributes={availableDataAttributes}
    />
  }

  return (
      <Col gridGap={"24px"}>
        <Row alignItems={"top"} flex={"10"} gridGap={"16px"}>
          <Col gridGap={"16px"} flex={"6"}>
            <JsonLogicAuthoringComponent
                jsonLogicExpression={eventModelState?.eventRule}
                setJsonLogicExpression={(jsonLogicExpression: string) => {
                  dispatch(updateEventRule(jsonLogicExpression));
                }}
                attributes={availableDataAttributes}
                header={STRINGS_DISPLAYED.event_rule_label}
                placeholder={STRINGS_DISPLAYED.event_rule_placeholder}
            />

            <WithFlyout renderFlyout={availableDataAttributesFlyout}>
              {({ open }) => (
                  <Button
                      variant={ButtonVariant.Secondary}
                      onClick={() => {
                        onAvailableDataAttributesClick(open);
                      }}
                      size={ButtonSize.Small}
                      className={"width_fit_content"}
                  >
                    {STRINGS_DISPLAYED.see_data_attributes_button_text}
                  </Button>
              )}
            </WithFlyout>
          </Col>

          <Col flex={"4"} alignItems={"start"}>
            <TestEventRuleExpander />
          </Col>
        </Row>
        {(!isValidRule) && <InputFooter id="input-footer-1" className={"width_200_px"} error>
          {
            STRINGS_DISPLAYED.invalid_event_rule_message
          }
        </InputFooter>
        }
      </Col>
  );
}
